import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Bookmark, Filter, Search } from "lucide-react";
import { SCENARIOS, DRILLS } from "../data/mockData";
import { ScenarioCard, AccentDot, SectionHeader } from "../components/Shared";
import { useDashboardData } from "../../hooks/useDashboardData";

const TABS = [
  { id: "scenarios", label: "Scenarios" },
  { id: "drills", label: "Drills" },
  { id: "vocab", label: "Vocab review" },
  { id: "listening", label: "Listening" },
];

const VOCAB_DECK = [
  { word: "presupuesto", gloss: "budget", strength: 0.78 },
  { word: "tener ganas de", gloss: "to feel like", strength: 0.41 },
  { word: "perro", gloss: "dog", strength: 0.92 },
  { word: "atajo", gloss: "shortcut", strength: 0.31 },
  { word: "embotellamiento", gloss: "traffic jam", strength: 0.22 },
  { word: "amanecer", gloss: "sunrise", strength: 0.6 },
];

const LISTENING = [
  { id: "l1", title: "Casual market chatter", duration: "3:20", level: "B1" },
  { id: "l2", title: "Office stand-up", duration: "4:10", level: "B1" },
  { id: "l3", title: "Tech podcast intro", duration: "5:00", level: "B2" },
];

const Practice = () => {
  const { memory, isLoading } = useDashboardData();
  const navigate = useNavigate();
  const [tab, setTab] = useState("scenarios");
  const [q, setQ] = useState("");
  const [diff, setDiff] = useState("all");

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto space-y-6" data-testid="page-practice-loading">
        <div className="animate-pulse rounded-3xl bg-panel/10 h-48 w-full" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="animate-pulse rounded-2xl bg-panel/10 h-40" />
          ))}
        </div>
      </div>
    );
  }

  // Option A Wiring: Curate scenario presets, but dynamically tailor them to live memory
  const failedVocab = memory?.failed_vocabulary || [];
  const masteredVocab = memory?.mastered_vocabulary || [];
  const strengths = memory?.strengths || [];

  const dynamicScenarios = SCENARIOS.map((s, idx) => {
    const matchingWeakWord = failedVocab[idx % Math.max(1, failedVocab.length)];
    if (matchingWeakWord) {
      return {
        ...s,
        hook: `Designed to practice your active vocab: "${matchingWeakWord.term}" — currently flagged as a weak spot.`,
      };
    }
    return s;
  });

  const filteredScenarios = dynamicScenarios.filter((s) =>
    (q ? s.title.toLowerCase().includes(q.toLowerCase()) || s.tag.toLowerCase().includes(q.toLowerCase()) : true) &&
    (diff === "all" ? true : s.difficulty.toLowerCase().includes(diff.toLowerCase()))
  );

  // Build dynamic drills from failed vocabulary & strengths
  const dynamicDrills = [];
  failedVocab.slice(0, 2).forEach((v, i) => {
    dynamicDrills.push({ id: `d_vocab_${i}`, label: `Vocab: ${v.term}`, weak: true, accent: "cyan" });
  });
  strengths.slice(0, 2).forEach((s, i) => {
    dynamicDrills.push({ id: `d_strength_${i}`, label: `Reinforce: ${s}`, weak: false, accent: "blue" });
  });

  const activeDrills = dynamicDrills.length > 0 ? dynamicDrills : DRILLS;

  // Build dynamic vocabulary reviews from actual database history
  const activeVocabDeck = (failedVocab.length > 0 || masteredVocab.length > 0)
    ? [
        ...failedVocab.map(v => ({ word: v.term, gloss: "needs review", strength: 0.35, weak: true })),
        ...masteredVocab.map(v => ({ word: v.term, gloss: "mastered", strength: 0.88, weak: false }))
      ].slice(0, 8)
    : VOCAB_DECK;

  return (
    <div className="max-w-7xl mx-auto" data-testid="page-practice">
      {/* Hero */}
      <div className="rounded-3xl glass edge-glow p-6 sm:p-8 mb-6">
        <div className="section-label">Practice · designed for you</div>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mt-3">
          <h1 className="font-display text-[28px] sm:text-[36px] font-light tracking-tight text-ink leading-tight">
            What AURA wants <em>you</em> to practice today.
          </h1>
          <div className="flex items-center gap-2 px-3 py-2 rounded-full border border-panel/10 bg-elev/60 min-w-[260px]">
            <Search size={14} className="text-whisper-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find scenarios, drills…" className="bg-transparent outline-none text-[13.5px] text-ink w-full" data-testid="practice-search" />
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-2" data-testid="practice-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`pill ${tab === t.id ? "" : "border-panel/10 bg-panel/[0.02] text-whisper"}`} data-testid={`practice-tab-${t.id}`}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* Content */}
      {tab === "scenarios" && (
        <>
          <div className="flex items-center gap-2 flex-wrap mb-4">
            <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted flex items-center gap-1.5"><Filter size={12} /> level</span>
            {["all", "A2", "B1", "B2"].map((d) => (
              <button key={d} onClick={() => setDiff(d)} className={`pill text-[10px] ${diff === d ? "" : "border-panel/10 bg-panel/[0.02] text-whisper"}`} data-testid={`practice-difficulty-${d}`}>{d}</button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="practice-scenarios">
            {filteredScenarios.map((s, i) => (
              <motion.div key={s.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.04 }}>
                <ScenarioCard s={s} onStart={() => navigate("/dashboard/session", { state: { scenario: s } })} />
              </motion.div>
            ))}
          </div>
        </>
      )}

      {tab === "drills" && (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="practice-drills">
          {activeDrills.map((d) => (
            <div key={d.id} className="glass rounded-2xl p-5 hairline flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <AccentDot accent={d.accent} />
                <div>
                  <div className="font-display text-[16px] text-ink">{d.label}</div>
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted mt-0.5">
                    {d.weak ? "weak spot · prioritized" : "spaced review"}
                  </div>
                </div>
              </div>
              <button onClick={() => navigate("/dashboard/session", { state: { drill: d } })} className="btn-ghost text-[10px] py-2 px-4"><Play size={11} /> start</button>
            </div>
          ))}
        </div>
      )}

      {tab === "vocab" && (
        <>
          <SectionHeader eyebrow="Vocab review" title="Spaced repetition · powered by your memory." />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="practice-vocab">
            {activeVocabDeck.map((v) => (
              <div key={v.word} className="glass rounded-2xl p-4 hairline">
                <div className="font-display text-[20px] text-ink leading-tight">{v.word}</div>
                <div className="text-whisper text-[13px] mt-1">{v.gloss}</div>
                <div className="mt-3 h-1.5 rounded-full bg-panel/5 overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-aura-cyan to-aura-blue" style={{ width: `${v.strength * 100}%` }} />
                </div>
                <div className="mt-2 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">{Math.round(v.strength * 100)}% owned</div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "listening" && (
        <>
          <SectionHeader eyebrow="Listening" title="Sound her out." />
          <div className="space-y-3" data-testid="practice-listening">
            {LISTENING.map((l) => (
              <div key={l.id} className="glass rounded-2xl p-5 hairline flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <button onClick={() => navigate("/dashboard/session", { state: { listening: l } })} className="w-11 h-11 rounded-full grid place-items-center bg-aura-cyan text-[#02121A]"><Play size={14} /></button>
                  <div>
                    <div className="font-display text-[17px] text-ink">{l.title}</div>
                    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted mt-1">{l.level} · {l.duration}</div>
                  </div>
                </div>
                <button className="btn-ghost text-[10px] py-2 px-3"><Bookmark size={11} /> save</button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Practice;
